/**
 * @description Hook utilizado para realizar requisições externas
 * @author Diego Sabino Amorim de Araújo
 * @file useFetch.js
 * @license Copyright (c) 2022 SIGEP DIGETI ITEP/RN
 */

import Cookies from 'js-cookie'
import axios from 'utils/axios'
import { criarAlerta } from 'actions'
import { useErrorService } from 'hooks/useErrorService'
import useRouter from 'utils/useRouter'
import { useDispatch } from 'react-redux'

export const useFetch = () => {
  const dispatch = useDispatch()
  const router = useRouter()
  const errorService = useErrorService()

  const calcularDiferencaMinutos = (dataAnterior, dataAtual) => {
    const diferencaMilisegundos = dataAtual - dataAnterior

    return Math.round(((diferencaMilisegundos % 86400000) % 3600000) / 60000)
  }
  const renewToken = async () => {
    try {
      const oldToken = Cookies.get('auth-sigep')

      const tempoToken = new Date(Cookies.get('auth-tempo'))
      const tempoAtual = new Date()
      let diferencaMinutos = 0
      if (tempoToken) {
        diferencaMinutos = calcularDiferencaMinutos(tempoToken, tempoAtual)
      }

      if (!tempoToken || diferencaMinutos > 60) {
        const response = await axios.post(
          `/auth/redefinir`,
          {},
          {
            headers: {
              Authorization: `Bearer ${oldToken}`,
            },
          }
        )

        if (response && response.data && response.data.token) {
          Cookies.set('auth-sigep', response.data.token)
          Cookies.set('auth-tempo', new Date())
        }
      }
    } catch (err) {
      console.log(err) //eslint-disable-line
    }
  }

  const get = async (url) => {
    try {
      const token = Cookies.get('auth-sigep')
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      renewToken()

      return response
    } catch (err) {
      errorService.tratarErro(err)
      throw err
    }
  }

  const getWithoutFeedback = async (url) => {
    try {
      const token = Cookies.get('auth-sigep')
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      renewToken()

      return response
    } catch (err) {
      throw err
    }
  }

  const getWithOptions = async (url, options) => {
    try {
      const token = Cookies.get('auth-sigep')
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        ...options,
      })
      renewToken()
      return response
    } catch (err) {
      errorService.tratarErro(err)
      throw err
    }
  }

  const getWithOptionsWithoutFeedback = async (url, options) => {
    try {
      const token = Cookies.get('auth-sigep')
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        ...options,
      })
      renewToken()
      return response
    } catch (err) {
      throw err
    }
  }

  const put = async (url, dados = null) => {
    try {
      const token = Cookies.get('auth-sigep')
      const response = await axios.put(
        url,
        { dados },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      dispatch(criarAlerta(response, router))
      renewToken()
      return response
    } catch (err) {
      errorService.tratarErro(err)
      throw err
    }
  }

  const putOpen = async (url, dados = null) => {
    try {
      const token = Cookies.get('auth-sigep')
      const response = await axios.put(url, dados, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      dispatch(criarAlerta(response, router))
      renewToken()
      return response
    } catch (err) {
      errorService.tratarErro(err)
      throw err
    }
  }

  const putWithoutFeedback = async (url, dados = null) => {
    try {
      const token = Cookies.get('auth-sigep')
      const response = await axios.put(
        url,
        { dados },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      renewToken()
      return response
    } catch (err) {
      errorService.tratarErro(err)
      throw err
    }
  }

  const putOpenWithoutFeedback = async (url, dados = null) => {
    try {
      const token = Cookies.get('auth-sigep')
      const response = await axios.put(url, dados, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      renewToken()
      return response
    } catch (err) {
      errorService.tratarErro(err)
      throw err
    }
  }

  const del = async (url, dados = null) => {
    try {
      const token = Cookies.get('auth-sigep')

      const response = await axios.delete(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          ...dados,
        },
      })

      dispatch(criarAlerta(response))
      renewToken()
      return response
    } catch (err) {
      errorService.tratarErro(err)
      throw err
    }
  }

  const delWithoutFeedback = async (url) => {
    try {
      const token = Cookies.get('auth-sigep')
      const response = await axios.delete(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      renewToken()
      return response
    } catch (err) {
      errorService.tratarErro(err)
      throw err
    }
  }

  const post = async (url, dados) => {
    try {
      const token = Cookies.get('auth-sigep')
      const response = await axios.post(
        url,
        { dados },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      dispatch(criarAlerta(response))
      renewToken()
      return response
    } catch (err) {
      errorService.tratarErro(err)
      throw err
    }
  }

  const postWithoutFeedback = async (url, dados) => {
    try {
      const token = Cookies.get('auth-sigep')
      const response = await axios.post(
        url,
        { dados },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      renewToken()
      return response
    } catch (err) {
      errorService.tratarErro(err)
      throw err
    }
  }

  const postOpenWithoutRenewToken = async (url, dados) => {
    try {
      const token = Cookies.get('auth-sigep')
      const response = await axios.post(url, dados, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      dispatch(criarAlerta(response))
      return response
    } catch (err) {
      errorService.tratarErro(err)
      throw err
    }
  }

  const postOpen = async (url, dados) => {
    try {
      const token = Cookies.get('auth-sigep')

      const response = await axios.post(url, dados, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      dispatch(criarAlerta(response))
      renewToken()
      return response
    } catch (err) {
      errorService.tratarErro(err)
      throw err
    }
  }

  const postOpenWithOptions = async (url, dados, options) => {
    try {
      const token = Cookies.get('auth-sigep')
      const response = await axios.post(url, dados, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        ...options,
      })
      dispatch(criarAlerta(response))
      return response
    } catch (err) {
      errorService.tratarErro(err)
      throw err
    }
  }

  const postOpenWithOptionsWithoutFeedback = async (url, dados, options) => {
    try {
      const token = Cookies.get('auth-sigep')
      const response = await axios.post(url, dados, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        ...options,
      })
      renewToken()
      return response
    } catch (err) {
      errorService.tratarErro(err)
      throw err
    }
  }

  const postOpenWithoutFeedback = async (url, dados) => {
    try {
      const token = Cookies.get('auth-sigep')
      const response = await axios.post(url, dados, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      renewToken()

      return response
    } catch (err) {
      errorService.tratarErro(err)
      throw err
    }
  }

  const postOpenWithoutFeedbackAutenticar = async (url, dados) => {
    try {
      const token = Cookies.get('auth-sigep')
      const response = await axios.post(url, dados, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      renewToken()

      return response
    } catch (err) {
      if (err.response && err.response.status === 401) {
        return null
      }
      errorService.tratarErro(err)
      throw err
    }
  }

  return {
    get,
    getWithoutFeedback,
    getWithOptions,
    getWithOptionsWithoutFeedback,
    put,
    putWithoutFeedback,
    putOpen,
    del,
    delWithoutFeedback,
    post,
    postOpen,
    postOpenWithoutFeedback,
    postOpenWithOptions,
    postOpenWithOptionsWithoutFeedback,
    postOpenWithoutRenewToken,
    postWithoutFeedback,
    putOpenWithoutFeedback,
    postOpenWithoutFeedbackAutenticar,
  }
}
